import { Diagnostic, Geotab } from "@fairfleet/geotab";
import { QueryObserverResult, useQueries } from "@tanstack/react-query";
import { uniq } from "lodash";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_DIAGNOSTIC } from "@/constants/resultsLimits";

function combineDiagnostics(results: QueryObserverResult<Diagnostic | null, Error>[]) {
  const data: Record<string, Diagnostic> = {};
  let hasLoaded = false;
  for (const result of results) {
    if (result.data) {
      data[result.data.id] = result.data;
      hasLoaded = true;
    }
  }
  return {
    data: hasLoaded ? data : undefined,
    isLoading: results.some(result => result.isLoading),
    isPending: results.some(result => result.isPending),
    isFetching: results.some(result => result.isFetching),
    isError: results.some(result => result.isError),
    error: results.find(result => result.error)?.error ?? undefined,
  };
}

const MAX_IDS_PER_GET = 1000;

interface PendingLoad {
  resolve: (diagnostic: Diagnostic | null) => void;
  reject: (error: unknown) => void;
}

const pendingByClient = new WeakMap<Geotab, Map<string, PendingLoad[]>>();

/**
 * Loads one diagnostic, coalescing every id requested in the same tick into a single `Get`.
 * Each id keeps its own cache entry, but a page that needs N diagnostics costs one call of the
 * Geotab client's rate-limit budget instead of N.
 */
function loadDiagnostic(geotabClient: Geotab, id: string): Promise<Diagnostic | null> {
  return new Promise((resolve, reject) => {
    let pending = pendingByClient.get(geotabClient);
    if (!pending) {
      pending = new Map();
      pendingByClient.set(geotabClient, pending);
    }
    const waiters = pending.get(id);
    if (waiters) {
      waiters.push({ resolve, reject });
      return;
    }
    const first = pending.size === 0;
    pending.set(id, [{ resolve, reject }]);
    if (first) {
      queueMicrotask(() => void flush(geotabClient));
    }
  });
}

async function flush(geotabClient: Geotab): Promise<void> {
  const batch = pendingByClient.get(geotabClient);
  if (!batch) return;
  pendingByClient.delete(geotabClient);

  const ids = [...batch.keys()];
  for (let start = 0; start < ids.length; start += MAX_IDS_PER_GET) {
    const chunk = ids.slice(start, start + MAX_IDS_PER_GET);
    void geotabClient.get("Diagnostic", { ids: chunk }, RESULTS_LIMIT_DIAGNOSTIC, new AbortController().signal).then(
      diagnostics => {
        const byId = new Map(diagnostics.map(diagnostic => [diagnostic.id, diagnostic]));
        for (const id of chunk) {
          for (const waiter of batch.get(id) ?? []) waiter.resolve(byId.get(id) ?? null);
        }
      },
      error => {
        for (const id of chunk) {
          for (const waiter of batch.get(id) ?? []) waiter.reject(error);
        }
      },
    );
  }
}

/**
 * Loads diagnostics by id, one cached query per id, so adding an id to the list costs one request
 * instead of refetching every diagnostic. Ids requested together are fetched in one `Get`.
 * `data` holds the diagnostics loaded so far.
 */
export function useDiagnosticsByIds(diagnosticIds?: string[], disabled?: boolean) {
  const ids = uniq(diagnosticIds);
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQueries({
    queries: ids.map(id => ({
      queryKey: ["diagnostic", activeSessionId, id],
      queryFn: (): Promise<Diagnostic | null> => loadDiagnostic(geotabClient, id),
      enabled: !disabled,
    })),
    combine: combineDiagnostics,
  });
}
